<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "~/assets/api/openapi"

const operationStore = useOperationStore()
const { startLoading, stopLoading } = useLoading()
const { getDocumentNumberForAHOperation } = useAppApi()

const documentNumber = computed<string | undefined>(() => {
  return operationStore.findFormDataValueByTextKey<
    components["schemas"]["StringValue"]
  >("gsb.ah.applicationDataFields.Urkundennummer")?.value
})

const findAndSetDocumentNumber = (
  entries: {
    textKey: string
    value: any
  }[],
  newValue: string
) => {
  for (const entry of entries) {
    if (entry.textKey === "gsb.ah.applicationDataFields.Urkundennummer") {
      entry.value = {
        ...entry.value,
        value: newValue,
      }
      return entries
    }

    if (entry.value && Array.isArray(entry.value.entries)) {
      if (findAndSetDocumentNumber(entry.value.entries, newValue)) {
        return entries
      }
    }
  }
  return false
}

const schema = yup.object().shape({
  documentNumber: yup.string().required("Pflichtfeld"),
  shipped: yup.boolean().test(
    "is-checked",
    "Der Versand muss bestätigt werden",
    yupTestOnlyOnCreate((value) => value === true)
  ),
  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>({
  ...cloneDeep(currentWorkStepData.value),
  documentNumber: documentNumber.value ?? "",
})

const generateDocumentNumber = async () => {
  if (!operationStore.operation) {
    return
  }
  try {
    startLoading()

    const { status, data } = await getDocumentNumberForAHOperation(
      operationStore.operation.id
    )

    if (status === 200 && data) {
      state.documentNumber = data.documentNumber
      const entries = findAndSetDocumentNumber(
        operationStore.operation.formData.entries ?? [],
        data.documentNumber
      )
      if (entries) {
        operationStore.operation = {
          ...operationStore.operation,
          formData: {
            ...operationStore.operation.formData,
            entries: entries,
          },
        }
        showToast("success", {
          description:
            "Die Urkundennummer wurde in den Vorgangsdaten gespeichert.",
        })
      } else {
        showToast("danger", {
          description: `Es ist ein Fehler bei der Anzeige der Urkundennummer in den Vorgangsdaten aufgetreten (KEY_NOT_FOUND). Bitte versuchen Sie es später erneut.`,
        })
      }
    } else {
      showToast("danger", {
        description: `Es ist ein Fehler bei der Erzeugung der Urkundennummer aufgetreten (NO_DATA). Bitte versuchen Sie es später erneut.`,
      })
    }
  } catch (error) {
    showToast("danger", {
      description: `Es ist ein Fehler bei der Erzeugung der Urkundennummer aufgetreten (API_ERROR). Bitte versuchen Sie es später erneut.`,
    })
  } finally {
    stopLoading()
  }
}

watch(
  () => state.shipped,
  (value) => {
    if (value === false) {
      state.shippingConfirmationDate = undefined
    }
    operationStore.unsavedChanges = false
  },
  { immediate: true }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Approbationsurkunde"
      />
    </template>

    <IffContentSection headline="Urkundennummer">
      <IffCopyText size="small" class="mb-3">
        Vor Erstellung der Approbationsurkunde muss die nächste verfügbare
        Urkundennummer ermittelt werden. Nutzen Sie dafür bitte den folgenden
        Button.
      </IffCopyText>
      <UFormGroup name="documentNumber" label="Urkundennummer" required>
        <UButtonGroup size="sm" orientation="horizontal">
          <UInput
            v-model="state.documentNumber"
            aria-required="true"
            class="whitespace-normal max-w-[300px]"
            readonly
            :ui="{ base: '!bg-gray-100 opacity-75 cursor-not-allowed' }"
            maxlenghth="100"
          />

          <UButton
            @click="generateDocumentNumber()"
            variant="outline"
            class="bg-white"
            :disabled="!!documentNumber"
          >
            Urkundennummer erzeugen
          </UButton>
        </UButtonGroup>
      </UFormGroup>
    </IffContentSection>

    <IffContentSection headline="Bestätigen" :is-last-child="true">
      <UFormGroup name="shipped">
        <UCheckbox
          required
          label="Der Versand der erforderlichen Dokumente an die antragstellende Person ist erfolgt."
          v-model="state.shipped"
          aria-required="true"
          aria-controls="shipping-confirmation-date-region"
        />
      </UFormGroup>
      <div id="shipping-confirmation-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            name="shippingConfirmationDate"
            label="Datum Approbationsurkunde"
            v-if="state.shipped"
            required
            class="max-w-[300px] w-fit"
          >
            <template #hint>
              <UButton
                @click="
                  state.shippingConfirmationDate = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.shippingConfirmationDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
